<template>
  <div class="membership-config-page">
    <section class="page-toolbar">
      <div>
        <strong>会员套餐配置</strong>
        <span>修改后小程序会员中心会直接读取最新价格与规格，下单金额以服务端配置为准。</span>
      </div>
      <div class="toolbar-actions">
        <button class="ghost-button" type="button" :disabled="loading || saving" @click="loadConfig">
          {{ loading ? "加载中..." : "刷新" }}
        </button>
        <button class="primary-button" type="button" :disabled="loading || saving" @click="saveConfig">
          {{ saving ? "保存中..." : "保存配置" }}
        </button>
      </div>
    </section>

    <section v-if="error" class="state-card state-card--error">{{ error }}</section>
    <section v-else-if="loading" class="state-card">正在加载会员套餐...</section>

    <template v-else>
      <section class="config-card plan-card">
        <div class="section-head">
          <div>
            <strong>套餐基础信息</strong>
            <span>套餐编码：{{ plan.code || "-" }}</span>
          </div>
          <select v-model.number="plan.status" class="status-select">
            <option :value="1">上架</option>
            <option :value="0">下架</option>
          </select>
        </div>
        <div class="form-grid">
          <label class="form-field">
            <span>套餐名称</span>
            <input v-model.trim="plan.name" maxlength="64" placeholder="例如：标准版会员">
          </label>
          <label class="form-field">
            <span>会员时长（月）</span>
            <input v-model.number="plan.durationMonths" type="number" min="1" max="36" step="1">
          </label>
          <label class="form-field form-field--wide">
            <span>套餐说明</span>
            <textarea v-model.trim="plan.description" maxlength="255" placeholder="展示会员包含的权益说明"></textarea>
          </label>
        </div>
      </section>

      <section class="config-card">
        <div class="section-head">
          <div>
            <strong>小鼠规格与价格</strong>
            <span>共 {{ specs.length }} 个规格。用户报价与供应商成本价分开设置，供应商应收按成本价加运费计算。</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="spec-table">
            <thead>
              <tr>
                <th>规格</th>
                <th>冻鼠报价（元）</th>
                <th>供应商成本价（元）</th>
                <th>运费（元）</th>
                <th>数量（只）</th>
                <th>排序</th>
                <th>库存</th>
                <th>履约店铺</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="spec in specs" :key="String(spec.id)">
                <td data-label="规格">
                  <input v-model.trim="spec.weightLabel" class="cell-input cell-input--label" maxlength="32">
                </td>
                <td data-label="冻鼠报价">
                  <div class="price-input-wrap">
                    <span>¥</span>
                    <input v-model.number="spec.priceAmount" class="cell-input" type="number" min="0" max="99999.99" step="0.01">
                  </div>
                </td>
                <td data-label="供应商成本价">
                  <div class="price-input-wrap">
                    <span>¥</span>
                    <input v-model.number="spec.costPriceAmount" class="cell-input" type="number" min="0" max="99999.99" step="0.01">
                  </div>
                </td>
                <td data-label="运费">
                  <div class="price-input-wrap">
                    <span>¥</span>
                    <input v-model.number="spec.shippingFeeAmount" class="cell-input" type="number" min="0" max="99999.99" step="0.01">
                  </div>
                </td>
                <td data-label="数量">
                  <input v-model.number="spec.quantity" class="cell-input cell-input--short" type="number" min="1" max="999" step="1">
                </td>
                <td data-label="排序">
                  <input v-model.number="spec.sortNo" class="cell-input cell-input--short" type="number" step="1">
                </td>
                <td data-label="库存">{{ spec.stockQuantity == null ? "-" : spec.stockQuantity }}</td>
                <td data-label="履约店铺">{{ spec.storeName || "-" }}</td>
                <td data-label="状态">
                  <select v-model.number="spec.status" class="status-select status-select--small">
                    <option :value="1">启用</option>
                    <option :value="0">停用</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="config-note">
        用户实付仍由平台统一收取；供应商应收 = 成本价 + 运费。保存报价时会同步更新履约商品展示价，库存不会自动修改，已创建订单保留原金额快照。
      </section>
    </template>

    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }">{{ toast }}</div>
  </div>
</template>

<script>
import { fetchMembershipConfig, updateMembershipConfig } from "../api/adminSystem"

export default {
  name: "MembershipConfigView",
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      plan: {},
      specs: [],
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  mounted() {
    this.loadConfig()
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer)
  },
  methods: {
    async loadConfig() {
      if (this.loading) return
      this.loading = true
      this.error = ""
      try {
        const response = await fetchMembershipConfig()
        const data = response.data || {}
        this.plan = {
          id: data.id,
          code: data.code || "",
          name: data.name || "",
          durationMonths: Number(data.durationMonths || 3),
          description: data.description || "",
          status: Number(data.status === 0 ? 0 : 1)
        }
        this.specs = Array.isArray(data.specs) ? data.specs.map(item => ({
          ...item,
          priceAmount: Number(item.priceAmount || 0),
          costPriceAmount: Number(item.costPriceAmount || 0),
          shippingFeeAmount: Number(item.shippingFeeAmount || 0),
          quantity: Number(item.quantity || 0),
          sortNo: Number(item.sortNo || 0),
          status: Number(item.status === 0 ? 0 : 1)
        })) : []
      } catch (error) {
        this.error = error.message || "会员套餐加载失败"
      } finally {
        this.loading = false
      }
    },
    async saveConfig() {
      if (this.saving) return
      if (!String(this.plan.name || "").trim()) {
        this.showToast("套餐名称不能为空", true)
        return
      }
      if (!this.specs.length) {
        this.showToast("至少需要保留一个小鼠规格", true)
        return
      }
      const invalidSpec = this.specs.find(spec => (
        !String(spec.weightLabel || "").trim()
        || Number(spec.priceAmount) < 0
        || Number(spec.costPriceAmount) < 0
        || Number(spec.shippingFeeAmount) < 0
        || Number(spec.quantity) <= 0
      ))
      if (invalidSpec) {
        this.showToast("请检查规格名称、价格和数量", true)
        return
      }
      this.saving = true
      try {
        const response = await updateMembershipConfig({
          plan: {
            name: String(this.plan.name || "").trim(),
            durationMonths: Number(this.plan.durationMonths),
            description: String(this.plan.description || "").trim(),
            status: Number(this.plan.status)
          },
          specs: this.specs.map(spec => ({
            id: Number(spec.id),
            weightLabel: String(spec.weightLabel || "").trim(),
            priceAmount: Number(spec.priceAmount),
            costPriceAmount: Number(spec.costPriceAmount),
            shippingFeeAmount: Number(spec.shippingFeeAmount || 0),
            quantity: Number(spec.quantity),
            sortNo: Number(spec.sortNo),
            status: Number(spec.status)
          }))
        })
        const data = response.data || {}
        this.plan = { ...this.plan, ...data }
        this.specs = Array.isArray(data.specs) ? data.specs : this.specs
        this.showToast("会员套餐已保存")
      } catch (error) {
        this.showToast(error.message || "会员套餐保存失败", true)
      } finally {
        this.saving = false
      }
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => {
        this.toast = ""
      }, 3200)
    }
  }
}
</script>

<style scoped>
.membership-config-page {
  max-width: 1280px;
  color: #26323e;
}

.page-toolbar,
.section-head,
.toolbar-actions {
  display: flex;
  align-items: center;
}

.page-toolbar,
.section-head {
  justify-content: space-between;
  gap: 16px;
}

.page-toolbar {
  align-items: flex-end;
  margin-bottom: 16px;
}

.page-toolbar strong,
.page-toolbar span,
.section-head strong,
.section-head span {
  display: block;
}

.page-toolbar strong {
  font-size: 18px;
}

.page-toolbar span,
.section-head span {
  margin-top: 6px;
  color: #7b8791;
  font-size: 12px;
  line-height: 18px;
}

.toolbar-actions {
  gap: 8px;
}

.config-card,
.state-card,
.config-note {
  padding: 18px;
  margin-bottom: 14px;
  border: 1px solid #e8edf1;
  border-radius: 12px;
  background: #fff;
}

.state-card--error {
  color: #a33c3c;
  border-color: #f1caca;
  background: #fff7f7;
}

.form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 14px;
  margin-top: 16px;
}

.form-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
}

.form-field--wide {
  grid-column: 1 / -1;
}

.form-field span {
  color: #55616c;
  font-size: 12px;
  font-weight: 600;
}

.form-field input,
.form-field textarea,
.cell-input,
.status-select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dde3e8;
  border-radius: 7px;
  background: #fbfcfd;
  color: #26323e;
  font: inherit;
}

.form-field input,
.status-select,
.cell-input {
  height: 38px;
  padding: 0 10px;
}

.form-field textarea {
  min-height: 72px;
  padding: 9px 10px;
  resize: vertical;
}

.status-select {
  width: 110px;
}

.status-select--small {
  width: 84px;
}

.table-wrap {
  margin-top: 14px;
  overflow-x: auto;
}

.spec-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
}

.spec-table th,
.spec-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #eef1f3;
  text-align: left;
  white-space: nowrap;
}

.spec-table th {
  color: #7a858e;
  font-size: 12px;
  font-weight: 600;
}

.spec-table td {
  color: #46515b;
  font-size: 13px;
}

.cell-input--label {
  min-width: 112px;
}

.cell-input--short {
  width: 82px;
}

.price-input-wrap {
  display: flex;
  width: 126px;
  align-items: center;
  gap: 5px;
}

.price-input-wrap span {
  color: #7a858e;
}

.config-note {
  color: #68747e;
  font-size: 12px;
  line-height: 19px;
  background: #f8fafb;
}

.toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 20;
  padding: 10px 16px;
  border-radius: 8px;
  background: #25313b;
  color: #fff;
  font-size: 13px;
}

.toast--error {
  background: #a73f3f;
}

@media (max-width: 720px) {
  .page-toolbar,
  .section-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field--wide {
    grid-column: auto;
  }
}
</style>
